import React, { useState } from 'react';
import { Settings } from 'lucide-react';

interface MessagingSettings {
    botToken?: string;
    signingSecret?: string;
    channels?: string;
    [key: string]: string | undefined;
}

interface MessagingIntegrationConfig {
    type: 'messaging';
    platform: 'slack';
    enabled: boolean;
    settings: MessagingSettings;
}

interface RAGIntegrationsProps {
    ragId: string;
    integrations: MessagingIntegrationConfig[];
    onUpdateIntegrations: (integrations: MessagingIntegrationConfig[]) => void;
}

// Slack logo SVG component
const SlackLogo = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6.2 15.4C6.2 16.7 5.1 17.8 3.8 17.8C2.5 17.8 1.4 16.7 1.4 15.4C1.4 14.1 2.5 13 3.8 13H6.2V15.4Z" fill="#E01E5A"/>
        <path d="M7.7 15.4C7.7 14.1 8.8 13 10.1 13C11.4 13 12.5 14.1 12.5 15.4V20.2C12.5 21.5 11.4 22.6 10.1 22.6C8.8 22.6 7.7 21.5 7.7 20.2V15.4Z" fill="#E01E5A"/>
        <path d="M10.1 6.2C8.8 6.2 7.7 5.1 7.7 3.8C7.7 2.5 8.8 1.4 10.1 1.4C11.4 1.4 12.5 2.5 12.5 3.8V6.2H10.1Z" fill="#36C5F0"/>
        <path d="M10.1 7.7C11.4 7.7 12.5 8.8 12.5 10.1C12.5 11.4 11.4 12.5 10.1 12.5H3.8C2.5 12.5 1.4 11.4 1.4 10.1C1.4 8.8 2.5 7.7 3.8 7.7H10.1Z" fill="#36C5F0"/>
        <path d="M20.2 10.1C20.2 8.8 21.3 7.7 22.6 7.7C23.9 7.7 25 8.8 25 10.1C25 11.4 23.9 12.5 22.6 12.5H20.2V10.1Z" fill="#2EB67D"/>
        <path d="M18.8 10.1C18.8 11.4 17.7 12.5 16.4 12.5C15.1 12.5 14 11.4 14 10.1V3.8C14 2.5 15.1 1.4 16.4 1.4C17.7 1.4 18.8 2.5 18.8 3.8V10.1Z" fill="#2EB67D"/>
        <path d="M16.4 20.2C17.7 20.2 18.8 21.3 18.8 22.6C18.8 23.9 17.7 25 16.4 25C15.1 25 14 23.9 14 22.6V20.2H16.4Z" fill="#ECB22E"/>
        <path d="M16.4 18.8C15.1 18.8 14 17.7 14 16.4C14 15.1 15.1 14 16.4 14H22.6C23.9 14 25 15.1 25 16.4C25 17.7 23.9 18.8 22.6 18.8H16.4Z" fill="#ECB22E"/>
    </svg>
);

type ConfigField = {
    key: keyof MessagingSettings;
    label: string;
    type: 'text' | 'password';
    placeholder?: string;
};

const configFields: ConfigField[] = [
    { key: 'botToken', label: 'Bot Token', type: 'password', placeholder: 'xoxb-your-token' },
    { key: 'signingSecret', label: 'Signing Secret', type: 'password' },
    { key: 'channels', label: 'Default Channels', type: 'text', placeholder: '#general,#support' }
];

export const RAGIntegrations: React.FC<RAGIntegrationsProps> = ({
                                                                    integrations,
                                                                    onUpdateIntegrations
                                                                }) => {
    const [showConfigModal, setShowConfigModal] = useState(false);
    const [configValues, setConfigValues] = useState<MessagingSettings>({});

    const currentIntegration = integrations[0] || {
        type: 'messaging' as const,
        platform: 'slack' as const,
        enabled: false,
        settings: {}
    };

    const handleConfigureClick = () => {
        setConfigValues(currentIntegration.settings);
        setShowConfigModal(true);
    };

    const handleSaveConfig = () => {
        const newConfig: MessagingIntegrationConfig = {
            type: 'messaging',
            platform: 'slack',
            enabled: true,
            settings: configValues
        };

        onUpdateIntegrations([newConfig]);
        setShowConfigModal(false);
    };

    return (
        <div className="card h-100">
            <div className="card-body d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                    <div className="me-3">
                        <SlackLogo />
                    </div>
                    <div>
                        <h6 className="mb-1">Slack</h6>
                        <p className="text-muted small mb-0">Connect your RAG to Slack channels</p>
                    </div>
                </div>
                <div className="d-flex align-items-center">
                    <div className="form-check form-switch me-3">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            checked={currentIntegration.enabled}
                            onChange={() => {
                                onUpdateIntegrations([{
                                    ...currentIntegration,
                                    enabled: !currentIntegration.enabled
                                }]);
                            }}
                        />
                    </div>
                    <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={handleConfigureClick}
                        disabled={!currentIntegration.enabled}
                    >
                        <Settings size={16} className="me-1" />
                        Configure
                    </button>
                </div>
            </div>

            {/* Configuration Modal */}
            {showConfigModal && (
                <div className="modal show d-block" tabIndex={-1}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Configure Slack Integration</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowConfigModal(false)}
                                    aria-label="Close"
                                />
                            </div>
                            <div className="modal-body">
                                {configFields.map((field) => (
                                    <div className="mb-3" key={field.key}>
                                        <label className="form-label fw-medium small mb-1">
                                            {field.label}
                                        </label>
                                        <input
                                            type={field.type}
                                            className="form-control border"
                                            value={configValues[field.key] || ''}
                                            onChange={(e) => setConfigValues({
                                                ...configValues,
                                                [field.key]: e.target.value
                                            })}
                                            placeholder={field.placeholder || ''}
                                            style={{ height: '38px' }}
                                        />
                                    </div>
                                ))}
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowConfigModal(false)}
                                    style={{ height: '38px' }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleSaveConfig}
                                    style={{ height: '38px' }}
                                >
                                    Save Configuration
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Backdrop */}
            {showConfigModal && (
                <div className="modal-backdrop show"></div>
            )}
        </div>
    );
};